if (!customElements.get('campaign-scroll-video')) {
  customElements.define(
    'campaign-scroll-video',
    class CampaignScrollVideo extends HTMLElement {
      connectedCallback() {
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.panels = Array.from(this.querySelectorAll('.campaign-scroll-video__panel'));
        this.dots = Array.from(this.querySelectorAll('.campaign-scroll-video__dot'));
        this.toggleButton = this.querySelector('.campaign-scroll-video__toggle');

        // 동작 줄이기를 켠 사용자에게는 자동 재생하지 않고, 버튼으로 직접 재생하게 합니다.
        this.userPaused = this.dataset.autoplay !== 'true' || this.motionQuery.matches;
        this.isVisible = false;
        this.centeredPanels = new Set();

        this.onMediaChange = this.syncPlayback.bind(this);
        this.onVisibilityChange = this.syncPlayback.bind(this);
        this.onToggleClick = this.togglePlayback.bind(this);
        this.onDotClick = this.handleDotClick.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);

        this.sectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isVisible = entry.isIntersecting;
            this.syncPlayback();
          },
          { threshold: 0 }
        );
        this.sectionObserver.observe(this);

        // 문안 페이드: 패널이 화면 가운데 50% 영역에 들어오면 보이게 합니다.
        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => entry.target.classList.toggle('is-visible', entry.isIntersecting));
          },
          { rootMargin: '-25% 0px -25% 0px', threshold: 0 }
        );

        // 현재 패널: 화면 세로 중앙선을 지나는 패널을 현재 패널로 봅니다.
        this.centerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.centeredPanels.add(entry.target);
              } else {
                this.centeredPanels.delete(entry.target);
              }
            });
            this.updateActivePanel();
          },
          { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
        );

        this.panels.forEach((panel) => {
          this.revealObserver.observe(panel);
          this.centerObserver.observe(panel);
        });

        this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
        this.toggleButton?.addEventListener('click', this.onToggleClick);
        this.mediaQuery.addEventListener('change', this.onMediaChange);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.classList.add('is-ready');
        this.syncPlayback();
      }

      disconnectedCallback() {
        this.sectionObserver?.disconnect();
        this.revealObserver?.disconnect();
        this.centerObserver?.disconnect();
        this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
        this.toggleButton?.removeEventListener('click', this.onToggleClick);
        this.mediaQuery?.removeEventListener('change', this.onMediaChange);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.querySelectorAll('video').forEach((video) => video.pause());
      }

      get activeVideo() {
        const slot = this.mediaQuery.matches
          ? '.campaign-scroll-video__media--mobile'
          : '.campaign-scroll-video__media--desktop';
        return this.querySelector(`${slot} video`);
      }

      syncPlayback() {
        const activeVideo = this.activeVideo;
        const shouldPlay = !this.userPaused && this.isVisible && !document.hidden;

        this.querySelectorAll('video').forEach((video) => {
          if (video !== activeVideo) video.pause();
        });

        if (activeVideo) {
          if (shouldPlay) {
            activeVideo.muted = true;
            activeVideo.preload = 'auto';
            const playPromise = activeVideo.play();
            if (playPromise) {
              playPromise.catch(() => {
                // 저전력 모드 등으로 자동 재생이 막히면 재생 버튼을 보여 줍니다.
                this.userPaused = true;
                this.updateToggle();
              });
            }
          } else {
            activeVideo.pause();
          }
        }

        this.updateToggle();
      }

      togglePlayback() {
        this.userPaused = !this.userPaused;
        this.syncPlayback();
      }

      updateToggle() {
        this.classList.toggle('is-paused', this.userPaused);
        if (!this.toggleButton) return;
        const label = this.userPaused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
        this.toggleButton.setAttribute('aria-label', label);
      }

      updateActivePanel() {
        const current = this.panels.find((panel) => this.centeredPanels.has(panel));
        this.classList.toggle('is-engaged', Boolean(current));
        if (!current) return;

        const activeIndex = this.panels.indexOf(current);
        this.dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      handleDotClick(event) {
        const index = Number(event.currentTarget.dataset.targetIndex);
        const panel = this.panels[index];
        if (!panel) return;
        panel.scrollIntoView({
          behavior: this.motionQuery.matches ? 'auto' : 'smooth',
          block: 'center',
        });
      }

      handleBlockSelect(event) {
        if (!this.contains(event.target)) return;
        event.target.classList.add('is-visible');
      }
    }
  );
}
